import React, { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState, } from 'react';
import { Box, useForkRef } from '@mui/material';
import { findNextFocusableElement } from './a11yUtils';
/**
 * Container for accessibility operations, restricting the keyboard Tab and Shift+Tab to cycle in the container or dismiss when tab out
 */
export const FocusLimitContainer = forwardRef((props, ref) => {
    const { children, focusBehavior = 'loop', autoFocus = true, autoFocusCallback, restoreFocus = true, restoreFocusCallback, onEscape, disableSentinel, shouldEscapeOnClickAway, ...others } = props;
    const originActiveElement = useRef(null);
    const refContainer = useRef(null);
    const combinedRefContainer = useForkRef(refContainer, ref);
    const refStart = useRef(null);
    const refEnd = useRef(null);
    const mountedRef = useRef(false);
    const [isFocusInside, setIsFocusInside] = useState(false);
    const updateIsFocusInside = useCallback(() => {
        setIsFocusInside(refContainer.current?.contains(document.activeElement) ?? false);
    }, []);
    const restoreFocusCallbackRef = useRef(restoreFocusCallback);
    const restoreFocusRef = useRef(restoreFocus);
    useEffect(() => {
        restoreFocusCallbackRef.current = restoreFocusCallback;
    }, [restoreFocusCallback]);
    useEffect(() => {
        restoreFocusRef.current = restoreFocus;
    }, [restoreFocus]);
    useLayoutEffect(() => {
        // We have to add a delay in case of the component is mounted in the mouseDown handler of the trigger,
        // which happen before the focus is set to the trigger
        setTimeout(() => {
            const start = refStart.current;
            const end = refEnd.current;
            const container = refContainer.current;
            mountedRef.current = true;
            if (!originActiveElement.current) {
                originActiveElement.current = document.activeElement;
            }
            if (typeof autoFocusCallback === 'function') {
                autoFocusCallback({ container, start, end });
            }
            else if (start && autoFocus) {
                const currentFocus = document.activeElement;
                // handle auto focus into the container
                if (container.contains(currentFocus) &&
                    currentFocus !== start &&
                    currentFocus !== end) {
                    return;
                }
                const first = findNextFocusableElement(start);
                if (!first ||
                    !container.contains(first) ||
                    first === start ||
                    first === end) {
                    return;
                }
                first.focus();
            }
        }, 0);
        updateIsFocusInside();
        document.body.addEventListener('focusin', updateIsFocusInside);
        document.body.addEventListener('focusout', updateIsFocusInside);
        return () => {
            document.body.removeEventListener('focusin', updateIsFocusInside);
            document.body.removeEventListener('focusout', updateIsFocusInside);
            const trigger = originActiveElement.current;
            if (typeof restoreFocusCallbackRef.current === 'function') {
                restoreFocusCallbackRef.current({
                    originActiveElement: trigger,
                });
            }
            else if (restoreFocusRef.current) {
                if (trigger && trigger !== document.body) {
                    // If the focus is inside the container, restore the focus onto the trigger
                    if (document.activeElement &&
                        // eslint-disable-next-line react-hooks/exhaustive-deps
                        refContainer.current?.contains(document.activeElement)) {
                        trigger.focus({ preventScroll: true });
                    }
                }
                else {
                    document.activeElement?.blur();
                }
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useLayoutEffect(() => {
        if (onEscape) {
            const handleMouseDown = (e) => {
                if (mountedRef.current &&
                    !refContainer.current?.contains(e.target)) {
                    if (!shouldEscapeOnClickAway || shouldEscapeOnClickAway(e)) {
                        onEscape(e);
                    }
                }
            };
            document.body.addEventListener('mousedown', handleMouseDown);
            document.body.addEventListener('touchstart', handleMouseDown);
            return () => {
                document.body.removeEventListener('mousedown', handleMouseDown);
                document.body.removeEventListener('touchstart', handleMouseDown);
            };
        }
        return undefined;
    }, [onEscape, shouldEscapeOnClickAway]);
    function focusToEdge(isEnd, e) {
        if (focusBehavior !== 'loop') {
            onEscape?.(e);
            return;
        }
        const refElement = isEnd ? refEnd.current : refStart.current;
        /* istanbul ignore else */
        if (refElement) {
            const target = findNextFocusableElement(refElement, isEnd);
            /* istanbul ignore next */
            // This should never happen since non-interactive content should never fullfil `isFocusInside`.
            // Keep the logic for robustness.
            if (target === (isEnd ? refStart.current : refEnd.current)) {
                document.activeElement?.blur();
                return;
            }
            target?.focus();
        }
    }
    const handleKeyDown = (e) => {
        const start = refStart.current;
        const end = refEnd.current;
        if (e.key === 'Tab' && !e.ctrlKey && !e.metaKey) {
            if (document.activeElement && !disableSentinel) {
                if (e.shiftKey) {
                    if (findNextFocusableElement(document.activeElement, true) === start) {
                        // previous
                        e.preventDefault();
                        e.stopPropagation();
                        focusToEdge(true, e);
                    }
                }
                else if (findNextFocusableElement(document.activeElement) === end) {
                    // next
                    e.preventDefault();
                    e.stopPropagation();
                    focusToEdge(false, e);
                }
            }
        }
        else if (e.key === 'Escape' && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
            onEscape?.(e);
        }
    };
    function renderSentinel(isEnd) {
        return (React.createElement(Box, { ref: isEnd ? refEnd : refStart, tabIndex: isFocusInside && !disableSentinel ? 0 : -1, "data-testid": isEnd ? 'sentinelEnd' : 'sentinelStart', "aria-hidden": true, sx: {
                position: 'absolute',
                pointerEvents: 'none',
                left: 0,
                right: 0,
                top: 0,
                bottom: 0,
                ':focus': {
                    outline: 'none',
                },
                ':focus-visible': {
                    outline: 'none',
                },
            }, onFocus: (e) => {
                // We add this fallback because some AT (e.g. VoiceOver) does not require tab keys to change focus
                if (refContainer.current?.getAttribute('aria-modal') === 'true') {
                    // Voice Over will take care of the focus if the container is modal while VO is on.
                    // It will focus on the first focusable element in that case, which is the sentinel.
                    // ignore that focus to avoid unexpected onClose call.
                    return;
                }
                focusToEdge(!isEnd, e);
            } }));
    }
    function handleFocus(e) {
        // In case of there being an autoFocus element inside the children,
        // in which the focus event fires before useLayoutEffect and we cannot grab the original focus there.
        if (!originActiveElement.current) {
            originActiveElement.current = e.relatedTarget;
        }
        others.onFocus?.(e);
    }
    return (React.createElement(Box, { ref: combinedRefContainer, onKeyDown: handleKeyDown, role: focusBehavior === 'loop' ? 'dialog' : undefined, sx: { position: 'relative' }, onFocus: handleFocus, ...others },
        renderSentinel(false),
        children,
        renderSentinel(true)));
});
